import type { ReactNode } from 'react';
import { Icon } from './Icon';

/* ==========================================================================
   قطعٌ مشتركةٌ بين ملفّ المندوب وصفحة «عمولاتي»
   ========================================================================== */

/**
 * الشاشتان تعرضان الرجلَ نفسَه لعينين مختلفتين.
 *
 * المديرُ يفتح ملفَّ مندوبٍ ليقرأ حالَه، والمندوبُ يفتح «عمولاتي» ليقرأ
 * حالَ نفسِه — والمعروضُ واحد: من هو، وكم استحقّ. فنسختان من الشكل تعنيان
 * أن يُصلَح عيبٌ في إحداهما ويبقى في الأخرى، وأن يرى الاثنان الرقمَ نفسَه
 * في هيئتين فيظنّاه رقمين.
 */

/** خانةُ بيانٍ تُقرأ ولا تُحرَّر — بأيقونةٍ تقول جنسَها قبل قراءة اسمها. */
export function Fact({
  label,
  value,
  icon,
  ltr,
  wide,
  title,
}: {
  label: string;
  value: ReactNode;
  icon: string;
  /** قيمةٌ لاتينيّةُ الاتّجاه — بريدٌ أو كودٌ أو هاتف. */
  ltr?: boolean;
  /** تأخذ خانتين: ما لا يسع سطرًا واحدًا في خانةٍ واحدة. */
  wide?: boolean;
  title?: string;
}) {
  return (
    <div className={`pf-item${wide ? ' is-wide' : ''}`}>
      <span className="pf-k">
        <Icon name={icon} size={12} />
        {label}
      </span>
      <span
        className={`pf-v${ltr ? ' num is-1line' : ''}`}
        dir={ltr ? 'ltr' : undefined}
        title={title ?? (ltr && typeof value === 'string' ? value : undefined)}
      >
        {value}
      </span>
    </div>
  );
}

/** شبكةُ البيانات — خيوطٌ رفيعةٌ تفصل خانةً عن أختها. */
export function FactGrid({ children }: { children: ReactNode }) {
  return <div className="pf-grid">{children}</div>;
}

/**
 * قوسُ إنجازٍ يتدرّج من الأحمر إلى الأخضر.
 *
 * واللونُ موضعٌ في السلّم لا امتدادٌ عليه: الثلثُ أحمرُ إلى برتقاليّ، ولا
 * يُدرَك الأخضرُ إلّا عند الاكتمال — كما يفعل شريطُ الهدف في الجداول، فلا
 * يختلف اللونان على الرقم نفسِه.
 */
export function AchieveRing({
  pct,
  caption = 'من الهدف',
  small,
  empty,
}: {
  pct: number;
  caption?: string;
  small?: boolean;
  /** بلا هدفٍ يُقاس عليه: قوسٌ رماديٌّ وكلمةٌ في جوفه. */
  empty?: string;
}) {
  const shown = Math.max(0, Math.min(100, pct));
  return (
    <span
      className={`ach-ring${small ? ' qz-ring' : ''}`}
      style={{ ['--ach-p' as string]: empty ? 0 : shown }}
      title={empty ?? `${pct}٪ ${caption}`}
    >
      <span className="ach-in">
        {empty ? (
          <span className="ach-cap">{empty}</span>
        ) : (
          <>
            <b className={`num ach-pct${pct >= 100 ? ' is-hit' : ''}`}>{pct}٪</b>
            <span className="ach-cap">{caption}</span>
          </>
        )}
      </span>
    </span>
  );
}

/** بطاقةُ مبلغٍ ملوّنة: رقمٌ بلونه وشريطٌ يقول حصّته — يُقرأ قبل أن يُتهجّى. */
export function MoneyTile({
  tone,
  icon,
  label,
  value,
  share,
  note,
}: {
  tone: string;
  icon: string;
  label: string;
  value: string;
  share?: number;
  note: string;
}) {
  return (
    <div className="cz-cell" style={{ ['--cz' as string]: tone }}>
      <span className="cz-head">
        <span className="cz-dot">
          <Icon name={icon} size={12} />
        </span>
        <span className="cz-k">{label}</span>
      </span>
      <b className="num cz-v">{value}</b>
      {share != null && (
        <span className="cz-bar">
          <span style={{ width: `${Math.max(0, Math.min(100, share))}%` }} />
        </span>
      )}
      <span className="cz-note">{note}</span>
    </div>
  );
}

/** مبدّلُ سنةٍ — سهمان ورقمٌ بينهما، كما في سائر الشاشات. */
export function YearSwitch({
  year,
  onYear,
  max,
}: {
  year: number;
  onYear: (y: number) => void;
  max?: number;
}) {
  const ceiling = max ?? new Date().getFullYear() + 1;
  return (
    <span className="seg" role="group" aria-label="السنة">
      <button
        type="button"
        className="seg-btn"
        onClick={() => onYear(year - 1)}
        title="السنة السابقة"
      >
        <Icon name="chevronRight" size={13} />
      </button>
      <span className="seg-btn is-on num">{year}</span>
      <button
        type="button"
        className="seg-btn"
        onClick={() => onYear(Math.min(ceiling, year + 1))}
        disabled={year >= ceiling}
        title="السنة التالية"
      >
        <Icon name="chevronLeft" size={13} />
      </button>
    </span>
  );
}
